<template>
	<div>
		<div class="flex flex_column flex_between modal-content modal-content--in-group">
			<div class="flex flex_column w_60 margin_top">
				<form>
					<h4 class="text_left font_bold margin_bottom">{{ $t('common.generalInformation') }}</h4>

					<div class="form-group">
						<CountrySelect
							:labelText="$t('debtorManagement.addDebtor.applicableLaw.label')"
							v-model="form.applicableLaw"
							:companyType="enumDef.companyType.DebtorCompany"
							:required="true"
							data-vv-name="applicableLaw"
							v-validate="modelValidations.applicableLaw"
							:error="errors.first('applicableLaw')"
						></CountrySelect>
					</div>

					<div class="form-group">
						<md-field :class="[{ 'md-error': errors.has('historicalPaymentDays')}]">
							<label>{{ $t('debtorManagement.addDebtor.historicalPaymentDays') }}</label>
							<MoneyAmountInput v-model="form.historicalPaymentDays"
											  data-vv-name="historicalPaymentDays"
											  v-validate="modelValidations.historicalPaymentDays"
											  :decimals="0"
											  required
							></MoneyAmountInput>
							<span v-if="errors.has('historicalPaymentDays')" class="validation-error">{{ errors.first('historicalPaymentDays') }}</span>
						</md-field>
					</div>

					<div class="form-group">
						<md-field :class="[{'md-error': errors.has('vatInPercent')}]">
							<label>{{ $t('debtorManagement.addDebtor.vatInPercent') }}</label>
							<MoneyAmountInput v-model="form.vatInPercent" data-vv-name="vatInPercent"
											  v-validate="modelValidations.vatInPercent" required></MoneyAmountInput>
							<span v-if="errors.has('vatInPercent')" class="validation-error">{{ errors.first('vatInPercent') }}</span>
						</md-field>
					</div>

					<div class="form-group">
						<DatePicker
							v-model="form.existsSince"
							:placeholder="$t('debtorManagement.addDebtor.existsSince.label')"
							data-vv-name="existsSince"
							v-validate="modelValidations.existsSince"
							required
							:error="errors.first('existsSince')"
						></DatePicker>
					</div>

					<hr class="separator"/>

					<h4 class="text_left font_bold margin_bottom margin_top">{{ $t('common.additionalData') }}</h4>

					<div class="form-group">
						<md-field>
							<label>{{ $t('debtorManagement.addDebtor.customerReference.label') }}</label>
							<md-input
								v-model="form.customerReference"
							></md-input>
						</md-field>
					</div>

					<div class="form-group">
						<div class="flex">
							<md-field class="flex_2 margin_r">
								<label>{{ $t('debtorManagement.addDebtor.salesRevenue') }}</label>
								<MoneyAmountInput v-model="form.revenueLastTwelveMonths"
								></MoneyAmountInput>
							</md-field>
							<CurrencySelect class="flex_1" v-model="form.revenueCurrency" :labelText="$t('common.currency')"/>
						</div>
					</div>

					<div class="form-group">
						<div class="flex">
							<md-field class="flex_1 margin_r">
								<label>{{ $t('debtorManagement.addDebtor.shareOfSales') }}</label>
								<MoneyAmountInput v-model="form.shareOfSalesFrom"
								></MoneyAmountInput>
							</md-field>
							<md-field class="flex_1">
								<label>{{ $t('debtorManagement.addDebtor.shareOfSalesTo') }}</label>
								<MoneyAmountInput v-model="form.shareOfSalesTo"
								></MoneyAmountInput>
							</md-field>
						</div>
					</div>

					<div class="margin_top">
						<div class="flex flex_items_center">
							<span class="margin_r">{{ $t("debtorManagement.addDebtor.offerDiscount") }}:</span>
							<md-radio
								v-model="form.offerDiscount"
								:value="true"
							>{{ $t("common.yes") }}
							</md-radio>
							<md-radio
								v-model="form.offerDiscount"
								:value="false"
							>{{ $t("common.no") }}
							</md-radio>
						</div>

						<div class="flex" v-show="form.offerDiscount === true">
							<md-field class="flex_1 margin_r">
								<label>{{ $t('debtorManagement.addDebtor.discountRate') }}</label>
								<MoneyAmountInput v-model="form.discountRate"
								></MoneyAmountInput>
							</md-field>
							<md-field class="flex_1">
								<label>{{ $t('debtorManagement.addDebtor.discountDays') }}</label>
								<md-input v-model="form.discountDays"></md-input>
							</md-field>
						</div>
					</div>
				</form>
			</div>
		</div>

		<div class="flex flex_end w_100 margin_top">
			<template v-if="isEditMode">
				<md-button :class="{ 'md-primary': isFormValid }" @click="confirmSubmitCreditManagement">
					{{ $t("common.save") }}
				</md-button>
			</template>
			<template v-else>
				<md-button :class="{ 'md-primary': isFormValid }" @click="next">
					{{ $t("common.continue") }}
				</md-button>
			</template>
		</div>
	</div>
</template>

<script>
import MoneyAmountInput from "src/components/templates/common/MoneyAmountInput";
import TbSelect from "src/components/templates/common/TbSelect";
import {debtorManagementApi} from "src/common/api/debtorManagementApi";
import {validateForm, validateRequiredFields} from "src/validation/validation";
import {enumDef} from "src/common/enumDefs";
import CountrySelect from "src/components/templates/select/CountrySelect";
import CurrencySelect from "@/components/templates/select/CurrencySelect.vue";
import DatePicker from "@/components/templates/DatePicker.vue";

export default {
	props: {
		relationId: { type: Number, default: null }
	},
	components: { DatePicker, CurrencySelect, MoneyAmountInput, TbSelect, CountrySelect },
	mounted() {
		this.loadCreditManagement();
	},
	data() {
		return {
			enumDef: enumDef,
			form: {
				existsSince: null,
				applicableLaw: null,
				vatInPercent: null,
				revenueLastTwelveMonths: null,
				revenueCurrency: null,
				shareOfSalesFrom: null,
				shareOfSalesTo: null,
				historicalPaymentDays: null,
				offerDiscount: null,
				discountRate: null,
				discountDays: null,
				customerReference: null,
			},
			modelValidations: {
				existsSince: {
					required: true
				},
				applicableLaw: {
					required: true
				},
				historicalPaymentDays: {
					required: true
				},
				vatInPercent: {
					required: true
				}
			},
		};
	},
	methods: {
		loadCreditManagement() {
			if (!this.relationId) {
				return;
			}
			debtorManagementApi.inspection.getCreditManagement(this.relationId)
			.then(res => {
				this.form = { ...res };
			}).catch(this.toast)
		},
		getData() {
			return this.form;
		},
		next() {
			validateForm(this, this.$validator).then(() => {
				this.$emit("next");
			}).catch(this.toast)
		},
		confirmSubmitCreditManagement() {
			validateForm(this, this.$validator).then(() => {
				let option = {
					title: this.$t("debtorManagement.editDebtor.confirm.title") ,
					msg: this.$t("debtorManagement.editDebtor.confirm.text"),
					confirmFun: this.submitCreditManagement
				};
				this.confirmMeg(option, true);
			}).catch(this.toast);
		},
		submitCreditManagement() {
			let credits = { ...this.form };
			let params = { id: this.relationId, credits: credits };
			debtorManagementApi.operation.saveDebtorCredit(params)
			.then(() => {
				this.$emit("relation-changed");
				this.toastSuccess();
			}).catch(this.toast)
		},
	},
	computed: {
		isEditMode() {
			return this.relationId;
		},
		isFormValid() {
			return validateRequiredFields(this.form, this.modelValidations);
		}
	}
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/base_variables.scss";

.md-radio {
	width: auto;
	margin: 0 10px 0 10px;
	display: inline-flex;
	position: relative;
}

.form-group {
	margin-bottom: 12px;
}

.separator {
	border: 0;
	border-top: 1px solid $gray-very-light;
	margin-top: 40px;
	width: 100%;
}

</style>